import React from "react";

export const Card = ({ className = "", children, testid, id }) => (
  <div
    id={id}
    data-testid={testid}
    className={`rounded-[24px] bg-white/[0.03] ring-1 ring-white/[0.07] ${className}`}
  >
    {children}
  </div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <div className={`text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80 ${className}`}>
    {children}
  </div>
);

export const PageHead = ({ eyebrow, title, sub, right }) => (
  <div className="flex items-end justify-between gap-4 flex-wrap">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-1.5">{title}</h1>
      {sub && <p className="text-[12px] text-white/40 mt-1.5">{sub}</p>}
    </div>
    {right}
  </div>
);

export const Tile = ({ label, value, hint, tone = "default", testid }) => (
  <div
    data-testid={testid}
    className="rounded-[24px] bg-white/[0.028] ring-1 ring-white/[0.07] p-5 hover:bg-white/[0.05] transition-colors duration-200"
  >
    <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/35 mb-2.5">{label}</div>
    <div
      className={`font-display text-2xl sm:text-[32px] leading-none tracking-tight ${
        tone === "good" ? "text-emerald-300" : tone === "warn" ? "text-amber-300" : "text-white"
      }`}
    >
      {value}
    </div>
    {hint && <div className="text-[11px] text-white/35 mt-2">{hint}</div>}
  </div>
);

export const Bars = ({ data = [], valueKey = "value", labelKey = "label", height = 96, testid }) => {
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  return (
    <div data-testid={testid}>
      <div className="flex items-end gap-1.5" style={{ height }}>
        {data.map((d, i) => (
          <div key={i} className="flex-1 min-w-0 group relative flex flex-col justify-end h-full">
            <span
              title={`${d[labelKey]}: ${d[valueKey]}`}
              className="w-full rounded-t bg-gradient-to-t from-emerald-600/35 to-emerald-400 min-h-[4px] transition-[height] duration-500"
              style={{ height: `${Math.max(5, ((d[valueKey] || 0) / max) * 100)}%` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export const AreaLine = ({ points = [], height = 140, testid, stroke = "#34d399" }) => {
  if (points.length === 0) return null;
  const max = Math.max(1, ...points);
  const w = 100;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(2)} ${(100 - (p / max) * 100).toFixed(2)}`)
    .join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height }} className="w-full" data-testid={testid}>
      <defs>
        <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L ${w} 100 L 0 100 Z`} fill="url(#ag)" />
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export const Pill = ({ children, tone = "muted", className = "" }) => {
  const tones = {
    live: "bg-emerald-500/15 ring-1 ring-emerald-400/35 text-emerald-300",
    good: "bg-emerald-500/10 ring-1 ring-emerald-400/25 text-emerald-300/90",
    warn: "bg-amber-400/10 ring-1 ring-amber-400/30 text-amber-300",
    bad: "bg-red-500/10 ring-1 ring-red-400/30 text-red-300",
    muted: "bg-white/[0.04] ring-1 ring-white/[0.09] text-white/45",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-[9.5px] tracking-[0.14em] uppercase ${tones[tone]} ${className}`}>
      {children}
    </span>
  );
};

export const Empty = ({ icon: Icon, text, testid }) => (
  <div className="rounded-[24px] ring-1 ring-dashed ring-white/10 py-14 text-center" data-testid={testid}>
    {Icon && <Icon className="w-8 h-8 text-white/20 mx-auto mb-3" />}
    <p className="text-white/40 text-sm">{text}</p>
  </div>
);

export const Toggle = ({ on, onClick, label, testid, hint }) => (
  <button onClick={onClick} data-testid={testid} className="w-full flex items-center justify-between gap-4 group">
    <span className="text-left">
      <span className="block text-[12.5px] text-white/75 group-hover:text-white">{label}</span>
      {hint && <span className="block text-[10.5px] text-white/30 mt-0.5">{hint}</span>}
    </span>
    <span
      className={`w-10 h-[22px] rounded-full relative shrink-0 transition-colors duration-200 ${
        on ? "bg-emerald-500" : "bg-white/[0.14] ring-1 ring-white/10"
      }`}
    >
      <span
        className="absolute top-[3px] w-4 h-4 rounded-full bg-black/85 transition-[left] duration-200"
        style={{ left: on ? 21 : 3 }}
      />
    </span>
  </button>
);

export const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—";

export const fmtClock = (iso) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—";

export const fmtMins = (m) => {
  if (!m) return "0m";
  const h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m`;
};

export const ago = (iso) => {
  if (!iso) return "—";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
